import React from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { NextPage } from 'next'
import { ContentfulProvider } from '@pcln/contentful-components'
import { PartnerProvider } from '@pcln/whitelabel-components'
import { FeatureProvider } from '@pcln/feature-components'
import { FLIGHT_PRODUCT_TYPE } from '@/constants/products'
import { CustomContext, PageProps, QueryString } from '@/types'
import {
  getPropsForAppBanner,
  shouldHideAppBanner
} from '@/components/AppBanner/utils'
import BANNER_TEXT_VALUES from '@/components/AppBanner/constants'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import { prePopulateFlightValues } from '../server/utils/prepop-helper'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import fetchSignInStatus from '../server/utils/signin-status'
import getCookies from '../server/utils/get-cookies'
import getPclnHeaders from '../server/utils/get-pcln-headers'
import setiService from '../server/middleware/seti'
import mapSetiResponse from '../utils/map-seti-response'

const AppBanner = dynamic(() => import('@/components/AppBanner'))
const appBannerValues = BANNER_TEXT_VALUES[FLIGHT_PRODUCT_TYPE]

const Layout = dynamic(() => import('@/components/Layout'))
const Flight = dynamic(() => import('@/components/Flights'))
const PartnerLayout = dynamic(() => import('@/components/WhiteLabel/Layout'))
const PartnerFlight = dynamic(() => import('@/components/WhiteLabel/Flights'))

const Flights: NextPage<PageProps> = ({
  headerHTML,
  footerHTML,
  bootstrapData
}) => {
  const { query } = useRouter()
  const { env, featureToggles, partner = null } = bootstrapData
  const shouldShowAppBanner = !shouldHideAppBanner(
    bootstrapData,
    query as QueryString
  )
  return (
    <FeatureProvider features={featureToggles}>
      <ContentfulProvider contentful={bootstrapData.contentful}>
        <PartnerProvider appName="m-fly-search" partner={partner} env={env}>
          {partner?.isWLPartner ? (
            <PartnerLayout partner={partner}>
              <PartnerFlight />
            </PartnerLayout>
          ) : (
            <>
              {shouldShowAppBanner && (
                <AppBanner
                  {...{
                    ...getPropsForAppBanner(bootstrapData),
                    ...appBannerValues
                  }}
                />
              )}
              <Layout
                headerHTML={headerHTML}
                footerHTML={footerHTML}
                partner={partner}
              >
                <Flight />
              </Layout>
            </>
          )}
        </PartnerProvider>
      </ContentfulProvider>
    </FeatureProvider>
  )
}

export async function getServerSideProps(ctx: CustomContext) {
  const { req, res } = ctx
  const pricelineSpecificHeaders = getPclnHeaders(req)

  const bootstrapData = generateBootstrapData(req, res)
  const { webstats, referral, appVersion } = bootstrapData
  const { clientGUID: cguid } = webstats

  const [
    flightPrePopData,
    pclnHeaderFooterContent,
    signInStatus,
    setiResponseEvents
  ] = await Promise.all([
    prePopulateFlightValues(ctx.query as QueryString),
    fetchPCLNHeaderFooterContent(req, res),
    fetchSignInStatus(
      cguid,
      getCookies(req).dmc || '',
      pricelineSpecificHeaders
    ),
    setiService(req, {
      appVersion,
      allowSetiOverrides: true,
      webstats,
      referral,
      reqQueryParams: ctx.query,
      ...(res.locals.additionalExperiments && {
        additionalExperiments: res.locals.additionalExperiments
      }),
      ...(res.locals.setiActiveTests && {
        setiActiveTests: res.locals.setiActiveTests
      })
    })
  ])
  const prePopData = flightPrePopData ? { flights: flightPrePopData } : null

  return {
    props: {
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapData,
        signInStatus,
        fullSETI: setiResponseEvents,
        seti: mapSetiResponse(setiResponseEvents),
        ...(prePopData && { prePopData })
      }
    }
  }
}

export default Flights
